import React, { useState, useMemo } from 'react';
import { LogEntry, Stats } from '../types';
import { DownloadIcon, EditIcon, SparklesIcon } from './ui/Icons';
import { dataService } from '../services/firebase';
import { categorizeActivity } from '../services/geminiService';

interface HistoryProps {
  logs: LogEntry[];
  onEdit: (entry: LogEntry) => void;
}

const History: React.FC<HistoryProps> = ({ logs, onEdit }) => {
  // Use local date to ensure "today" matches the user's perception
  const getTodayLocal = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offset).toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState<string>(getTodayLocal());
  const [isProcessingAI, setIsProcessingAI] = useState<string | null>(null);

  // Filter logs by date
  const filteredLogs = useMemo(() => {
    return logs.filter(log => log.date === selectedDate);
  }, [logs, selectedDate]);

  // Calculate Stats
  const stats: Stats = useMemo(() => {
    return filteredLogs.reduce((acc, log) => {
      if (log.type === 'focus') {
          acc.totalFocusMinutes += log.duration / 60;
          acc.sessionsCompleted += 1;
      } else if (log.type === 'rest') {
          acc.totalRestMinutes += log.duration / 60;
          acc.sessionsCompleted += 1;
      }
      // We do not add 'unplanned' to focus/rest minutes, but it exists in the log list
      return acc;
    }, {
      totalFocusMinutes: 0,
      totalRestMinutes: 0,
      sessionsCompleted: 0,
      currentStreak: 0 // Streak logic requires multi-day analysis, simplified here for daily
    });
  }, [filteredLogs]);

  const handleExportCSV = () => {
    const headers = ['Date', 'Start Time', 'End Time', 'Type', 'Category', 'Duration (min)', 'Notes'];
    const rows = filteredLogs.map(log => [
      log.date,
      new Date(log.startTime).toLocaleTimeString(),
      new Date(log.endTime).toLocaleTimeString(),
      log.type,
      log.category || 'Uncategorized',
      (log.duration / 60).toFixed(1),
      `"${log.notes.replace(/"/g, '""')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `mindul_flow_export_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAICategorize = async (entry: LogEntry) => {
    setIsProcessingAI(entry.id);
    const category = await categorizeActivity(entry.notes);
    await dataService.updateLog(entry.id, { category });
    setIsProcessingAI(null);
  };

  // Unique dates for dropdown
  const availableDates = useMemo(() => {
    const dates = new Set(logs.map(l => l.date));
    const sorted = Array.from(dates).sort().reverse();
    const today = getTodayLocal();
    if (!sorted.includes(today)) {
        sorted.unshift(today);
    }
    return sorted;
  }, [logs]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
            <h2 className="text-xl font-bold text-slate-800">Activity Log</h2>
            <p className="text-slate-500 text-sm">Review your daily progress</p>
        </div>
        
        <div className="flex gap-2">
            <select 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            >
                {availableDates.map(date => (
                    <option key={date} value={date}>
                        {new Date(date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric'})}
                    </option>
                ))}
            </select>
            <button 
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors"
            >
                <DownloadIcon className="w-4 h-4" />
                Export
            </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100">
            <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Focus Time</span>
            <div className="text-2xl font-bold text-emerald-900">{stats.totalFocusMinutes.toFixed(0)} <span className="text-sm font-normal">min</span></div>
        </div>
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
            <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Rest Time</span>
            <div className="text-2xl font-bold text-amber-900">{stats.totalRestMinutes.toFixed(0)} <span className="text-sm font-normal">min</span></div>
        </div>
        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
            <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Sessions</span>
            <div className="text-2xl font-bold text-indigo-900">{stats.sessionsCompleted}</div>
        </div>
         <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Productivity</span>
            <div className="text-2xl font-bold text-slate-700">
                {stats.totalFocusMinutes + stats.totalRestMinutes > 0 
                  ? Math.round((stats.totalFocusMinutes / (stats.totalFocusMinutes + stats.totalRestMinutes)) * 100) 
                  : 0}%
            </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full text-left border-collapse">
            <thead>
                <tr className="text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-3 px-2 font-semibold whitespace-nowrap">Time</th>
                    <th className="py-3 px-2 font-semibold">Duration</th>
                    <th className="py-3 px-2 font-semibold">Type</th>
                    <th className="py-3 px-2 font-semibold w-5/12">Notes</th>
                    <th className="py-3 px-2 font-semibold">Category</th>
                    <th className="py-3 px-2 font-semibold text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                {filteredLogs.length === 0 ? (
                    <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-400">No activity recorded for this day.</td>
                    </tr>
                ) : (
                    filteredLogs.map(log => {
                        // Styling based on Type
                        let rowClass = "border-b border-slate-100 hover:bg-slate-50 group";
                        let badgeClass = "bg-slate-100 text-slate-700";
                        let badgeText = "Unplanned";

                        if (log.type === 'focus') {
                            badgeClass = "bg-emerald-100 text-emerald-800";
                            badgeText = "Focus";
                        } else if (log.type === 'rest') {
                            badgeClass = "bg-amber-100 text-amber-800";
                            badgeText = "Rest";
                        } else if (log.type === 'unplanned') {
                            rowClass = "border-b border-slate-100 bg-slate-50/50 hover:bg-slate-100 group text-slate-500 italic";
                            badgeClass = "bg-slate-200 text-slate-600";
                        }

                        return (
                        <tr key={log.id} className={rowClass}>
                            <td className="py-3 px-2 text-sm whitespace-nowrap align-top">
                                {new Date(log.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(log.endTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </td>
                            <td className="py-3 px-2 text-sm align-top">
                                {Math.floor(log.duration / 60)}m {log.duration % 60}s
                            </td>
                            <td className="py-3 px-2 align-top">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
                                    {badgeText}
                                </span>
                            </td>
                            <td className="py-3 px-2 text-sm whitespace-pre-wrap break-words align-top">
                                {log.notes}
                            </td>
                            <td className="py-3 px-2 align-top">
                                {log.category ? (
                                    <span className="text-xs font-medium bg-white text-slate-600 px-2 py-1 rounded-full border border-slate-200 whitespace-nowrap shadow-sm">
                                        {log.category}
                                    </span>
                                ) : (
                                    log.type !== 'unplanned' && (
                                        <button 
                                            onClick={() => handleAICategorize(log)}
                                            disabled={isProcessingAI === log.id}
                                            className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-800 disabled:opacity-50 whitespace-nowrap"
                                        >
                                            <SparklesIcon className="w-3 h-3" />
                                            {isProcessingAI === log.id ? '...' : 'AI Label'}
                                        </button>
                                    )
                                )}
                            </td>
                            <td className="py-3 px-2 text-right align-top">
                                <button 
                                    onClick={() => onEdit(log)}
                                    className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <EditIcon className="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    )})
                )}
            </tbody>
        </table>
      </div>
    </div>
  );
};

export default History;